import React from 'react'
import {useTranslation} from 'react-i18next'

import {useAppState} from '../../../state'
import styled from 'styled-components'
import {LabelCheckbox} from '../../Atoms/Forms'
import {P} from '../../Atoms/Typography'
import {normalizeSites} from '../../../config/utils'
import {colors} from '../../../sharedComponents/colors'
import {Site} from 'state/state'

interface Props {
  controller: {
    register: (
      id: string
    ) => {
      name: string
      checked: boolean
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    }
    list: {
      id: string
    }[]
    toggleAll: (all: Site[]) => void
    toggle: (id: string) => void
  }
  cyKey: string
  label: string
}
const LocationsList = ({controller, cyKey, label}: Props) => {
  const {state} = useAppState()
  const {t} = useTranslation('tasks')
  const rc = controller
  const chain = state.chainsById[state.selectedChainId!]
  const sites = normalizeSites(chain)
  const selectedVisibleCount = rc.list.filter(sel => sites.some(s => s.id === sel.id)).length
  const allVisibleSelected = sites.length > 0 && selectedVisibleCount === sites.length

  // Helper: determine if a site is currently selected
  const isSiteSelected = (id: string) => rc.list.some(sel => sel.id === id)

  // Toggle a set of sites without clobbering selections from other locations.
  const toggleSitesPreservingOthers = (targetSites: Site[]) => {
    const allSelected = targetSites.every(site => isSiteSelected(site.id))
    if (allSelected) {
      targetSites.forEach(site => {
        if (isSiteSelected(site.id)) rc.toggle(site.id)
      })
    } else {
      targetSites.forEach(site => {
        if (!isSiteSelected(site.id)) rc.toggle(site.id)
      })
    }
  }

  return !!chain ? (
    <RestaurantList>
      <div key={chain.id} data-cy={`${cyKey}-site-selector`}>
        <P variant="small">{label}</P>
        {selectedVisibleCount === 0 && (
          <P variant="small" style={{color: colors.system.red}}>
            {t('tasks:labels.noLocationMessage', 'You must select at least one location for the task')}
          </P>
        )}
        <LabelCheckbox
          flipped={false}
          checked={allVisibleSelected}
          name="selectAll"
          label={t('tasks:labels.all', 'All')}
          onClick={() => toggleSitesPreservingOthers(sites)}
          data-cy={`${cyKey}-select-all`}
        />
        {chain.brands.map(brand => {
          return (
            <div key={brand.id}>
              {brand.locationGroups.map(locGroup => {
                // Skip empty groups
                if (!locGroup.locations || locGroup.locations.length === 0) return null

                // Gather all sites across the group's locations
                const groupSites: Site[] = locGroup.locations.reduce((acc: Site[], l) => acc.concat(l.sites || []), [])

                const selectedCountForGroup = groupSites.filter((site: Site) => rc.list.some(sel => sel.id === site.id))
                  .length
                const allGroupSelected = groupSites.length > 0 && selectedCountForGroup === groupSites.length

                return (
                  <div key={locGroup.id} className="location-group">
                    <GroupHeader>
                      <LabelCheckbox
                        flipped={false}
                        checked={allGroupSelected}
                        name={`group-${locGroup.id}`}
                        label={locGroup.name || t('tasks:labels.group', 'Group')}
                        onClick={() => toggleSitesPreservingOthers(groupSites)}
                        data-cy={`${cyKey}-group-${locGroup.id}`}
                      />
                    </GroupHeader>

                    {locGroup.locations.map(loc => {
                      // Render a location-level checkbox that toggles all sites in this location
                      if (!loc.sites || loc.sites.length === 0) return null

                      const selectedCountForLocation = loc.sites.filter((site: Site) =>
                        rc.list.some(sel => sel.id === site.id)
                      ).length
                      const allLocationSelected = loc.sites.length > 0 && selectedCountForLocation === loc.sites.length

                      return (
                        <LocationWrapper key={loc.id}>
                          <LocationHeader>
                            <LabelCheckbox
                              flipped={false}
                              checked={allLocationSelected}
                              name={`location-${loc.id}`}
                              label={loc.name}
                              onClick={() => toggleSitesPreservingOthers(loc.sites)}
                              data-cy={`${cyKey}-location-${loc.id}`}
                            />
                          </LocationHeader>

                          <SitesWrapper>
                            {loc.sites.map((site: Site) => {
                              const reg = rc.register(site.id)
                              return (
                                <SiteItem key={site.id}>
                                  <LabelCheckbox
                                    flipped={false}
                                    label={loc.visible ? `${site.name}` : site.name}
                                    checked={reg.checked}
                                    name={reg.name}
                                    onClick={() => rc.toggle(site.id)}
                                    data-cy={`${cyKey}-site-${site.id}`}
                                  />
                                </SiteItem>
                              )
                            })}
                          </SitesWrapper>
                        </LocationWrapper>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </RestaurantList>
  ) : (
    <div>{t('common:errors.undefinedError', 'Something went wrong')}</div>
  )
}
const RestaurantList = styled.ul`
  padding: 1rem;
`

// New styled wrappers to add indentation and spacing
const LocationWrapper = styled.div`
  margin-top: 0.5rem;
`
const LocationHeader = styled.div`
  padding-left: 0.5rem;
`
const SitesWrapper = styled.div`
  padding-left: 1.5rem;
  margin-top: 0.25rem;
`
const SiteItem = styled.div`
  margin-top: 0.15rem;
`
const GroupHeader = styled.div`
  padding-left: 0.25rem;
  margin-top: 0.25rem;
`
export default LocationsList
